import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { setBlockChatInput } from '../actions/app-state';
import { validateFormField } from '../actions/conversation';
import { FORM_FIELD_TYPES } from '../constants/message';
import { getFormFieldErrorMessage } from '../utils/errors';
import { generateForm, generateFormResponse } from '../utils/message';
import FormFieldInputComponent from './FormFieldInputComponent';
import FormFieldSelectComponent from './FormFieldSelectComponent';

class FormMessage extends Component {
    static propTypes = {
        _id: PropTypes.string,
        className: PropTypes.string.isRequired,
        blockChatInput: PropTypes.bool,
        dispatch: PropTypes.func.isRequired,
        fields: PropTypes.array.isRequired,
        formErrors: PropTypes.shape({
            formErrorUnknown: PropTypes.string.isRequired,
            formErrorInvalidEmail: PropTypes.string.isRequired,
            formErrorNoShorterThan: PropTypes.string.isRequired,
            formErrorNoLongerThan: PropTypes.string.isRequired,
        }).isRequired,
        formFieldValues: PropTypes.object.isRequired,
        isFormSubmitted: PropTypes.bool.isRequired,
        isPrechatCaptureForm: PropTypes.bool.isRequired,
        latestAppUserMessageTimestamp: PropTypes.number.isRequired,
        linkColor: PropTypes.string.isRequired,
        received: PropTypes.number.isRequired,
        setFormFieldValue: PropTypes.func.isRequired,
        submitForm: PropTypes.func.isRequired,
        type: PropTypes.string.isRequired,
    };

    static defaultProps = {
        blockChatInput: false,
    };

    fieldRefs = {};

    state = {
        isFormSubmitted: this.props.isFormSubmitted,
    };

    componentDidMount() {
        if (
            this.props.blockChatInput &&
            !this.state.isFormSubmitted &&
            this.props.received > this.props.latestAppUserMessageTimestamp
        ) {
            this.props.dispatch(setBlockChatInput(true));
        }
    }

    focusNextField(currentField) {
        const currentFieldIndex = this.props.fields.findIndex((f) => f.name === currentField.name);
        let nextFieldIndex = this.props.fields.findIndex(
            (f, index) => !this.props.formFieldValues[f.name] && index > currentFieldIndex,
        );

        if (nextFieldIndex < 0) {
            nextFieldIndex = this.props.fields.findIndex((f) => !this.props.formFieldValues[f.name]);

            if (nextFieldIndex < 0) {
                return;
            }
        }

        const nextField = this.props.fields[nextFieldIndex];

        this.fieldRefs[nextField.name].focus();
    }

    onFieldSubmit = (field, { value, onValidationSuccess, onValidationError }) => {
        const onSuccess = () => {
            if (onValidationSuccess) {
                onValidationSuccess();
            }

            return this.props.setFormFieldValue(field.name, value, () =>
                this.processSuccessfulFieldSubmission(field, value),
            );
        };
        const onError = (error) => {
            if (onValidationError) {
                onValidationError(error);
            }
        };

        if (!value) {
            onError('This entry is required');
        } else {
            this.processFieldValidation(field, value).then(onSuccess, (error) => {
                const errorMessage = getFormFieldErrorMessage(error, this.props.formErrors);
                onError(errorMessage);
            });
        }
    };

    processFieldValidation = (formField, value) => {
        if (formField.type === 'select') {
            return Promise.resolve();
        }
        const { _id, blockChatInput, fields, isPrechatCaptureForm } = this.props;
        const form = generateForm(fields, { blockChatInput });
        const formResponse = generateFormResponse([formField], _id, { [formField.name]: value });

        return this.props.dispatch(validateFormField({ form, formResponse, isPrechatCaptureForm }));
    };

    processSuccessfulFieldSubmission = (field) => {
        const isFormCompleted = Object.keys(this.props.formFieldValues).length === this.props.fields.length;

        if (isFormCompleted) {
            this.props.submitForm();
        } else {
            this.focusNextField(field);
        }
    };

    render() {
        const { className, fields, formFieldValues, isFormSubmitted } = this.props;
        const containerStyle = {
            transition: 'border-top 0.3s ease-in-out',
            borderTop: `4px solid ${this.props.linkColor}`,
            ...(isFormSubmitted && { borderTop: `0px solid ${this.props.linkColor}` }),
        };

        if (fields.length <= 0) {
            return null;
        }

        const fieldsList = fields.map((field) => {
            const sharedProps = {
                assignRef: (ref) => {
                    this.fieldRefs[field.name] = ref;
                },
                isFormSubmitted,
                key: field.name,
                label: field.label,
                name: field.name,
                placeholder: field.placeholder,
                type: field.type,
            };

            if ([FORM_FIELD_TYPES.TEXT, FORM_FIELD_TYPES.EMAIL].includes(field.type)) {
                return (
                    <FormFieldInputComponent
                        {...sharedProps}
                        onFieldSubmit={(options) => this.onFieldSubmit(field, options)}
                        submittedFieldValue={formFieldValues[field.name] || ''}
                    />
                );
            } else if (field.type === FORM_FIELD_TYPES.SELECT) {
                const submittedFieldValue =
                    Array.isArray(formFieldValues[field.name]) && formFieldValues[field.name][0];

                return (
                    <FormFieldSelectComponent
                        {...sharedProps}
                        onChange={(values) => this.onFieldSubmit(field, { value: values })}
                        options={field.options}
                        submittedFieldValue={submittedFieldValue || null}
                    />
                );
            }
        });

        return (
            <div style={containerStyle} className={className}>
                <form>{fieldsList}</form>
            </div>
        );
    }
}

export default connect(
    ({
        config: { colors },
        conversations,
        appState: { activeConversationId },
        ui: {
            text: { formErrorUnknown, formErrorInvalidEmail, formErrorNoShorterThan, formErrorNoLongerThan },
        },
    }) => ({
        linkColor: `#${colors.linkColor}`,
        formErrors: {
            formErrorUnknown,
            formErrorInvalidEmail,
            formErrorNoShorterThan,
            formErrorNoLongerThan,
        },
        latestAppUserMessageTimestamp: conversations[activeConversationId].latestAppUserMessageTimestamp,
    }),
    undefined,
    undefined,
    { forwardRef: true },
)(FormMessage);
